import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios';
import router from '../router/index.js';

import { encrypt } from '@/util/rsa.js';
import redirect from '@/util/redirect.js';

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    isLogin: (localStorage.getItem('isLogin') ? true : false),
    user: JSON.parse(localStorage.getItem('user')) || null,
    
    userSearchKeyword: '', // 사용자 목록 검색어

    // 마지막 조회 상품 카테고리
    lastViewProductCategoryCode: localStorage.getItem('lastViewProductCategoryCode') ? localStorage.getItem('lastViewProductCategoryCode') : '',

    // 주문 정보
    orderDatas: JSON.parse(localStorage.getItem('orderDatas')) || [],
  },
  getters: {
  },
  mutations: {
    login: (state) => {
      state.isLogin = true;
    },
    logout: (state) => {
      state.isLogin = false;
    },
    setUser: (state, userObj) => {
      state.user = userObj;
    },
    
    setUserSearchKeyword: (state, keyword) => {
      state.userSearchKeyword = keyword;
    },

    setLastViewProductCategoryCode: (state, lastViewProductCategoryCode) => {
      state.lastViewProductCategoryCode = lastViewProductCategoryCode;
    },

    setOrderDatas: (state, orderDatas) => {
      state.orderDatas = orderDatas;
    },
  },
  actions: {
    async login({ commit }, userInfo) {
      // 로그인 API 호출
      const { status, data } = await loginAPI(userInfo.type, userInfo.id, userInfo.pwd);
      await handleLoginProcess(commit, status, data);
    },
    async encryptedLogin({ commit }, loginInfo) {
      // 암호화된 로그인 API 호출
      const { status, data } = await encryptedLoginAPI(loginInfo);
      await handleLoginProcess(commit, status, data);
    },
    async logout({ dispatch }) {
      // JWT 기반 인증에 최적화된 로그아웃 처리
      try {
        const resStatus = await logoutAPI();
        
        // 200: 정상 로그아웃, 401: 이미 토큰이 만료되어 로그아웃된 상태
        if (resStatus === 200 || resStatus === 401) {
          // 서버에서 토큰 삭제 완료 - 안전한 로그아웃
          dispatch('clearClientState');
          redirect.handleLogoutRedirect();
        } else {
          // 예상치 못한 서버 오류 - 재시도 요청
          console.error('서버 로그아웃 실패. 상태코드:', resStatus);
          alert('로그아웃 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        }
      } catch (err) {
        // 네트워크 에러 또는 서버 오류 - 재시도 요청
        console.error('로그아웃 API 호출 실패:', err);
        alert('서버 연결이 불안정합니다. 잠시 후 다시 시도해주세요.');
      }
    },
    /** 클라이언트 상태 완전 정리 (로그아웃 시 공통 사용) */
    clearClientState({ commit }) {
      localStorage.removeItem('user');
      localStorage.removeItem('isLogin');
      localStorage.removeItem('orderDatas');
      localStorage.removeItem('lastViewProductCategoryCode');
      commit('logout');
      commit('setUser', null);
    },
    setUserName({commit}, userName) {
      let userData = JSON.parse(localStorage.getItem('user'));
      if(userData.type === 0) { // 관리자 이름 변경인 경우
        userData.name = userName;
      } else if(userData.type === 1) { // 일반회원 이름 변경인 경우
        userData.company = userName;
      }
      localStorage.setItem('user', JSON.stringify(userData));
      commit('setUser', userData);
    },

    setUserSearchKeyword({commit}, keyword) {
      commit('setUserSearchKeyword', keyword);
    },

    setLastViewProductCategoryCode({commit}, lastViewProductCategoryCode) {
      localStorage.setItem('lastViewProductCategoryCode', lastViewProductCategoryCode);
      commit('setLastViewProductCategoryCode', lastViewProductCategoryCode);
    },

    setOrderDatas({commit}, orderDatas) {
      localStorage.removeItem('orderDatas');
      localStorage.setItem('orderDatas', JSON.stringify(orderDatas));
      commit('setOrderDatas', orderDatas);
    },
  },
  modules: {
  }
})

/** login API 호출 함수
 * @param {number} type 0: 영업자 아이디, 1: 고객 아이디
 * @param {string} id 아이디
 * @param {string} pwd 비밀번호 (평문)
 * @returns {object} { status, data } | status: HTTP 상태코드, data: 응답 데이터
 */
async function loginAPI(type, id, pwd) {
  let loginInfo = {
    type: (type === 0 || type === 1) ? type : -1, // 0/1만 허용. 그 외는 방어값(-1)
    id: id || '',
    pwd: pwd || '',
  };
  loginInfo = encrypt(JSON.stringify(loginInfo)); // 암호화
  
  const res = await axios.post('/api/auth/login',
    { loginInfo: loginInfo },
    { validateStatus: (status) => status < 500 },
  );
  return {status: res.status, data: res?.data || {}};
}

/** 암호화된 로그인 API 호출 함수
 * @param {string} loginInfo 암호화된 로그인 정보
 * @returns {object} { status, data } | status: HTTP 상태코드, data: 응답 데이터
 */
async function encryptedLoginAPI(loginInfo) {
  const res = await axios.post('/api/auth/check/encrypted-login', 
    { loginInfo: loginInfo }, 
    { validateStatus: (status) => status < 500 }
  );
  return { status: res.status, data: res?.data || {} };
}

/** 로그인 처리 공통 함수
 * @param {Function} commit - Vuex commit
 * @param {number} status - HTTP 상태코드
 * @param {Object} data - API 응답 데이터
 */
async function handleLoginProcess(commit, status, data) {
  if (status === 200) { // 로그인 성공
    const user = data.user;
    // 로그인 성공 처리 (기존 handleLoginSuccess 통합)
    localStorage.setItem('user', JSON.stringify(user));
    commit('setUser', user);
    localStorage.setItem('isLogin', true);
    commit('login');

    if (user.type === 0) {
      // 관리자 로그인 리디렉션 처리
      try { 
        if (window.location.pathname.includes('admin')) {
          router.replace({ name: 'admin-dashboard' }); 
        } else { // 일반 페이지인 경우 새로고침
          redirect.redirectTo(window.location.origin + window.location.pathname);
        }
      } catch (e) { /* empty */ }
      return;
    }
    // 일반회원은 현재 접속지가 소속 사이트인지 확인 후 필요 시 리디렉션
    await redirect.handleLoginRedirect();
  } else {
    // 로그인 에러 처리
    if (status === 423) {
      alert('아직 승인되지 않은 아이디 입니다.');
    } else if (status === 403) {
      alert('아이디/비밀번호를 확인해주세요.');
    }
  }
}

/** 로그아웃 API 호출 함수
 * @return {number} HTTP 상태코드
 */
async function logoutAPI() {
  const res = await axios.post('/api/auth/logout', {},
    { validateStatus: (status) => status < 500 },
  );
  return res.status;
}